import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import EmptyContent from 'components/EmptyContent';
import Preloader from 'components/Preloader';
import { useTranslation } from 'react-i18next';
import Table from 'ui-library/components/Table/Table';
import Title from 'ui-library/components/Title/Title';
import useListOfBundledDocuments from 'pages/DocLibraryPage/hooks/useListOfBundledDocuments';
import { Icon, Modal, notification } from 'ui-library';
import { ICON_TYPE_CLOSE } from 'ui-library/components/Icon/constants';
import { DocStatus } from 'components/DocBox/DocBox.types';
import DocBoxGeneric from 'components/DocBox/DocBoxGeneric';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';
import { LangSource, languagesAdapt } from 'translations/adaptors/languagesAdapt';
import ServiceManager from 'services/ServiceManager';
import { downloadDmsDocument } from 'utils/downloadDmsDocument';
import { BundleDocumentType } from '../BundledDocuments.types';

const ListOfBundledDocuments = ({
    bundledDocumentId,
    productOptions,
}: {
    bundledDocumentId: string;
    productOptions: {
        value: string;
        label: string;
    }[];
}) => {
    const { t, i18n: { language } } = useTranslation();
    const languages: LangSource[] = useMemo(
        () => languagesAdapt(globalThis.availableLanguages, t),
        [language],
    );

    const [showDocDetailsModal, setShowDocDetailsModal] = useState<any>(undefined);
    const [docDownloadInProgress, setDocDownloadInProgress] = useState<string | undefined>(undefined);
    const [uploadedFiles, setUploadedFiles] = useState<Record<string, File>>({});
    const [isSavingDocDetails, setIsSavingDocDetails] = useState(false);

    const { mutateAsync: handleMutation } = useGenericAPIMutation();

    const handleViewDetails = useCallback(async ({ item }) => {
        setShowDocDetailsModal({
            loading: item?.id,
        });

        const contractIds: string[] = Array.from(
            new Set(Object.values(item?.contractIds ?? {}).filter(Boolean) as string[]),
        );

        const responses: any[] = await Promise.all(
            Object.values(contractIds).map((contractId) => handleMutation({
                service: 'documents',
                API: 'postDocumentsList',
                payload: {
                    page: 1,
                    pageSize: 10,
                    contractId,
                    isActive: true,
                },
            })),
        );

        setShowDocDetailsModal({
            loading: null,
            id: item.id,
            title: `${item?.name}${item?.modelPortfolioRiskCategory ? ` - ${item?.modelPortfolioRiskCategory}` : ''} ${item?.modelPortfolioName ? ` | ${item?.modelPortfolioName}` : ''}${item?.securityIsin ? ` | ${item?.securityIsin}` : ''}${item?.isJointAccount ? ` - ${t('ApplicationDetails.JointAccount.Title')}` : ''}`,
            modelPortfolioName: item?.modelPortfolioName,
            modelPortfolioRiskCategory: item?.modelPortfolioRiskCategory,
            securityIsin: item?.securityIsin,
            securityName: item?.securityName,
            documentType: item?.documentType,
            documents: responses
                .flatMap((response) => response?.data?.results ?? [])
                .filter((doc) => doc?.contractId === item?.contractIds?.[doc?.languageTwoLetterIsoCode])
                .reduce((acc, doc) => {
                    acc[doc.languageTwoLetterIsoCode] = {
                        fileName: doc?.filename,
                        id: doc?.id,
                    };

                    return acc;
                }, {}),
        });
    }, [handleMutation, t]);

    const {
        productTitle,
        uploadModalTitle,
        listOfBundledDocuments,
        listOfBundledDocumentsColumns,
        ListOfBundledDocumentsLoading,
        ListOfBundledDocumentsLoadingError,
    } = useListOfBundledDocuments({
        bundledDocumentId,
        handleViewDetails,
        productOptions,
        isDocDataLoading: showDocDetailsModal?.loading || false,
    });

    const handleDocDownload = useCallback(async (item) => {
        setDocDownloadInProgress(item?.id);
        try {
            const { data: documentData } = await ServiceManager.documents('getDocumentById', [item?.id]) as any;

            const { newBlob } = downloadDmsDocument(documentData, {
                saveFile: true,
                name: item?.fileName || null,
            });

            const url = URL.createObjectURL(newBlob);

            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (err: any) {
            notification.open({
                content: `${t('advisoryDocument.somethingWentWrong')} ${err.message}`,
                type: 'error',
            });
        } finally {
            setDocDownloadInProgress(undefined);
        }
    }, []);

    const handleFileUpload = useCallback(
        (languageValue: string) => ({ files }: { files: File[] }) => {
            const [file] = files;

            if (!file) {
                return;
            }

            setUploadedFiles((prev) => ({ ...prev, [languageValue]: file }));
        },
        [],
    );

    const closeDocDetailsModal = useCallback(() => {
        setShowDocDetailsModal(undefined);
        setUploadedFiles({});
    }, []);

    const handleSaveDocDetails = useCallback(async () => {
        const files = Object.entries(uploadedFiles).map(([lang, file]) => ({
            language: lang,
            file,
        }));

        if (!files.length) {
            return;
        }

        setIsSavingDocDetails(true);
        try {
            await handleMutation({
                service: 'customDocumentsService',
                API: 'uploadStaticDocumentsInABundle',
                payload: {
                    files,
                    bundleDocumentIds: [showDocDetailsModal?.id],
                    language,
                },
                invalidateQueryKeys: ['getData_getAllStaticDocumentsInABundle'],
            });

            notification.open({
                content: t('advisoryDashboard.documents.viewDocumentModal.uploadSuccess', 'Documents uploaded successfully'),
                type: 'success',
            });
            closeDocDetailsModal();
        } catch (err: any) {
            notification.open({
                content: `${t('advisoryDocument.somethingWentWrong')} ${err?.message ?? ''}`,
                type: 'error',
            });
        } finally {
            setIsSavingDocDetails(false);
        }
    }, [uploadedFiles, handleMutation, showDocDetailsModal?.id, language, t, closeDocDetailsModal]);

    useEffect(() => () => setShowDocDetailsModal(undefined), []);

    const canUpload = useMemo(() => showDocDetailsModal?.documentType !== BundleDocumentType.Template, [showDocDetailsModal?.documentType]);

    return (
        <>
            <div>

                <Preloader
                    isLoading={ListOfBundledDocumentsLoading}
                    error={ListOfBundledDocumentsLoadingError}
                >
                    <EmptyContent
                        data={listOfBundledDocuments}
                        text={t('advisoryDashboard.noData')}
                    >
                        <Title type={3} className="mb-40 font-normal">
                            {productTitle || ''}
                        </Title>
                        <Table
                            className="Table--ext system-messages-table"
                            columns={listOfBundledDocumentsColumns}
                            data={listOfBundledDocuments}
                        />
                    </EmptyContent>
                </Preloader>
            </div>

            <Modal
                title={showDocDetailsModal?.title || ''}
                visible={!!showDocDetailsModal?.id}
                okText={t(
                    'advisoryDashboard.documents.viewDocumentModal.saveChanges',
                )}
                cancelText={t('clientDashboard.close')}
                onOk={handleSaveDocDetails}
                okButtonProps={{
                    disabled: Object.keys(uploadedFiles).length === 0,
                    hidden: !canUpload,
                    loading: isSavingDocDetails,
                }}
                cancelButtonProps={{ disabled: isSavingDocDetails }}
                onCancel={closeDocDetailsModal}
                width={730}
                height="80%"
                className="list-of-bundled-documents-modal"
                borderRadius
                closable
                closeIcon={<Icon type={ICON_TYPE_CLOSE} size={16} />}
            >
                <Title
                    type={3}
                    className="font-normal mb-32 list-of-bundled-documents-modal--product-title"
                >
                    {uploadModalTitle || ''}
                </Title>
                {(showDocDetailsModal?.modelPortfolioRiskCategory || showDocDetailsModal?.modelPortfolioName) && (
                    <Title type={3} className="font-normal mb-16">
                        <strong>
                            {t('advisoryDashboard.documents.viewDocumentModal.modelPortfolio', 'Model portfolio')}
                            {' '}
                            :
                        </strong>
                        {' '}
                        {showDocDetailsModal?.modelPortfolioRiskCategory ? `${showDocDetailsModal?.modelPortfolioRiskCategory}` : ' - '}
                        {showDocDetailsModal?.modelPortfolioName ? ` | ${showDocDetailsModal?.modelPortfolioName}` : ' - '}
                    </Title>
                )}

                {(showDocDetailsModal?.securityName || showDocDetailsModal?.securityIsin) && (
                    <Title type={3} className="font-normal mb-32">
                        <strong>
                            {t('advisoryDashboard.documents.viewDocumentModal.security', 'Security')}
                            {' '}
                            :
                        </strong>
                        {' '}
                        {showDocDetailsModal?.securityName ? `${showDocDetailsModal?.securityName}` : ' - '}
                        {showDocDetailsModal?.securityIsin ? ` | ${showDocDetailsModal?.securityIsin}` : ' - '}
                    </Title>
                )}

                <Title type={3} className="font-normal mb-24">
                    <strong>Documents</strong>
                </Title>

                {languages?.map(
                    (lang) => (
                        <div
                            key={lang?.value}
                            className="mb-16 flex gap-8 align-center"
                        >
                            <Title
                                type={3}
                                className="font-normal mb-8"
                                style={{ minWidth: 80 }}
                            >
                                {lang?.label}
                            </Title>

                            <DocBoxGeneric
                                docName={uploadedFiles?.[lang?.value]?.name ?? showDocDetailsModal?.documents?.[lang?.value]?.fileName}
                                uploadStatus={uploadedFiles?.[lang?.value] ? DocStatus.ReadyToSubmit : DocStatus.ReadyToUpload}
                                canDownload
                                isDownloadInProgress={docDownloadInProgress === showDocDetailsModal?.documents?.[lang?.value]?.id}
                                onDownload={() => handleDocDownload(showDocDetailsModal?.documents?.[lang?.value])}
                                canUpload={canUpload}
                                handleUpload={handleFileUpload(lang?.value)}
                            />
                        </div>
                    ),
                )}
            </Modal>
        </>
    );
};

export default ListOfBundledDocuments;
